import PropTypes from 'prop-types';

import Container, { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { useGetPageContentQuery } from '../../services/seoContent';
import { CircularProgress } from '@classity-edtech/ui-base';
import Helmet from '../../components/Helmet';
import Header from '../../components/SEO/Header';
import { Footer } from '../../components/SEO/Footer';
import { LegalComponent } from '@classity-edtech/ui-base/dist/components/Displays';
import { PolicySection } from '../PrivacyPolicy';
import { extractFooterContent, extractHeaderContent } from '../../utils/extractContents';

export interface TermsOfUseProps extends MuiContainerProps {}

export const TermsOfUse = ({ className }: TermsOfUseProps) => {
  const screens = useAppMediaQuery();
  const smallScreens = screens.isMobile || screens.isTablet;

  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'termsOfUsePage',
    contentType: 'page',
  });

  const extractContent = () => {
    if (!!data) {
      return {
        seo: {
          title: data?.seo?.fields?.titleTag,
          keywords: data?.seo?.fields?.keywords,
          description: data?.seo?.fields?.metaDescription,
          metaRobotsIndex: data?.seo?.fields?.metaRobotsIndex,
        },
      };
    }
  };

  const extractPolicyHeader = () => {
    const content = data?.sections?.[0];

    return {
      title: content?.fields?.title ?? '',
      effectiveDate: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
    };
  };

  const extractTextBlocks = description => {
    if (!description?.content) return [];

    return description.content.flatMap(block =>
      (block.content || [])
        .map(item => item?.value)
        .filter(v => typeof v === 'string' && v.trim() !== ''),
    );
  };

  const extractSections = (): PolicySection[] => {
    const sections = data?.sections?.slice(1, -1);

    const sectionMap = {};

    sections?.forEach(section => {
      const sectionTitle = section.fields?.title;
      if (!sectionTitle) return;

      if (!sectionMap[sectionTitle]) {
        sectionMap[sectionTitle] = {
          title: sectionTitle,
          content: [],
        };
      }

      (section.fields?.list || []).forEach(listItem => {
        const fields = listItem?.fields;
        const texts = extractTextBlocks(fields?.description);

        const content = [];
        const items = [];

        texts?.forEach(text => {
          if (text.endsWith(':') || texts?.length === 1) {
            content.push(text);
          } else {
            items.push(text);
          }
        });

        sectionMap[sectionTitle].content.push({
          subtitle: fields?.title || null,
          content,
          items,
        });
      });
    });

    return Object.values(sectionMap);
  };

  const extractContactInformation = () => {
    const content = data?.sections?.[data?.sections?.length - 1];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      contacts:
        content?.fields?.list?.[0]?.fields?.description?.content?.map(
          (block: any) => block?.content?.[0]?.value ?? '',
        ) ?? [],
    };
  };

  return (
    <>
      <Helmet
        title={extractContent()?.seo?.title}
        keywords={extractContent()?.seo?.keywords}
        description={extractContent()?.seo?.description}
        metaRobotsIndex={extractContent()?.seo?.metaRobotsIndex}
      />

      {fetchingContent ? (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Header {...extractHeaderContent(data)} hideNavigation={true} />

          <Box
            sx={{
              mt: { xs: 0, md: '-82px' },
              mb: 5,
            }}
          >
            <Container sx={{ pt: { xs: 5, md: 13 } }}>
              <LegalComponent
                header={extractPolicyHeader()}
                sections={extractSections()}
                contacts={extractContactInformation()}
              />
            </Container>
          </Box>

          <Footer {...extractFooterContent(data)} />
        </>
      )}
    </>
  );
};

TermsOfUse.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

TermsOfUse.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(TermsOfUse)(
  ({ theme }) => `
`,
);
